import React, { useState, useEffect, useCallback } from 'react';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { Link } from 'react-router-dom';
import * as XLSX from 'xlsx';
import axios from 'axios';
import Swal from 'sweetalert2';
import { useSelector } from 'react-redux';
import ComptableLayout from '../../layout/comptable-layout';
import DuoModal from '../../components/ui/duo-modal';
import DuoPeriodPicker from '../../components/ui/duo-period-picker';
import { APP_API_URL, APP_NAME } from '../../utils';
import formatNumber, { defaultFilterDates, DUO_PERIOD_OPTIONS, formatDateWithTime } from '../../utils/functions';

const PERIOD_ALL = { label: 'Tout', value: '' };
const PERIOD_TODAY =
  DUO_PERIOD_OPTIONS.find((p) => p.value === "aujourd'hui") ?? { label: "Aujourd'hui", value: "aujourd'hui" };
const periodOptionsWithAll = [PERIOD_ALL, ...DUO_PERIOD_OPTIONS];

const DEFAULT_FILTERS = {
  search: '',
  period: '',
  ...defaultFilterDates(),
  statut: '',
};

const SortIcon = ({ active, direction }) => (
  <span className="ms-1 opacity-75">{active ? (direction === 'asc' ? '↑' : '↓') : '↕'}</span>
);

const statusBadgeClass = (statut) => {
  if (statut === 'approuvé') return 'duo-badge-pill duo-badge-pill--info';
  if (statut === 'en cours') return 'duo-badge-pill duo-badge-pill--warn';
  if (statut === 'livré') return 'duo-badge-pill duo-badge-pill--ok';
  if (statut === 'annulé') return 'duo-badge-pill duo-badge-pill--off';
  return 'duo-badge-pill';
};

const statusLabel = (statut) => {
  if (statut === 'approuvé') return 'Approuvé';
  if (statut === 'en cours') return 'En cours';
  if (statut === 'livré') return 'Livré';
  if (statut === 'annulé') return 'Annulé';
  return statut;
};

export default function ComptableSalesPage() {
  const user = useSelector((state) => state.auth.user);
  const isRole13 = Number(user?.personnels?.[0]?.poste_id) === 13;

  const [showModal, setShowModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [libelle, setLibelle] = useState('');
  const [montant, setMontant] = useState('');

  const [data, setData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [responseMeta, setResponseMeta] = useState(null);
  const [metaExtra, setMetaExtra] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  const [isLoading, setIsLoading] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [selectedPeriod, setSelectedPeriod] = useState(PERIOD_ALL);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${APP_API_URL}/commandes`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          page: currentPage,
          sort_by: sortConfig.key,
          sort_direction: sortConfig.direction,
          period: filters.period,
          date_from: filters.dateFrom,
          date_to: filters.dateTo,
          search: filters.search,
          statut_commande: filters.statut || undefined,
          accounting_view: true,
        },
      });

      const responseData = response.data.data;
      setData(Array.isArray(responseData) ? responseData : []);
      setLastPage(response.data.meta?.last_page || 1);
      setResponseMeta(response.data.meta);
      setMetaExtra(response.data.meta_extra);
    } catch (error) {
      console.error('Erreur lors du chargement des ventes :', error);
      setData([]);
      setLastPage(1);
      setResponseMeta(null);
      setMetaExtra(null);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, sortConfig, filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePageChange = (direction) => {
    if (direction === 'next' && currentPage < lastPage) setCurrentPage((p) => p + 1);
    else if (direction === 'prev' && currentPage > 1) setCurrentPage((p) => p - 1);
  };

  const requestSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
    setCurrentPage(1);
  };

  const handlePeriodSelect = (option) => {
    setSelectedPeriod(option);
    setFilters((prev) => ({
      ...prev,
      period: option.value,
      dateFrom: '',
      dateTo: '',
    }));
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    setSelectedPeriod(PERIOD_ALL);
    setFilters({ ...DEFAULT_FILTERS });
    setSortConfig({ key: 'created_at', direction: 'desc' });
    setCurrentPage(1);
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    doc.text('Gestion des ventes', 20, 20);
    const headers = ['Référence', 'Montant TTC', 'Bénéfice', 'Client', 'Date', 'Statut'];
    const rows = data.map((item) => [
      item.designation,
      `${formatNumber(item.montant_net)} FCFA`,
      `${formatNumber(item.benefice)} FCFA`,
      item.customer?.name ?? '—',
      formatDateWithTime(item.date_creation),
      statusLabel(item.statut_commande),
    ]);
    doc.autoTable({ head: [headers], body: rows, startY: 30, headStyles: { fillColor: [88, 204, 2] } });
    doc.save(`Ventes ${APP_NAME}.pdf`);
  };

  const exportToExcel = () => {
    const worksheetData = data.map((item) => ({
      Référence: item.designation,
      'Montant TTC': `${formatNumber(item.montant_net)} FCFA`,
      Bénéfice: `${formatNumber(item.benefice)} FCFA`,
      Client: item.customer?.name ?? '—',
      Date: formatDateWithTime(item.date_creation),
      Statut: statusLabel(item.statut_commande),
    }));
    const ws = XLSX.utils.json_to_sheet(worksheetData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Ventes');
    XLSX.writeFile(wb, `Ventes ${APP_NAME}.xlsx`);
  };

  const handlePrint = () => {
    const printWindow = window.open('', '', 'height=800,width=1200');
    const tableRows = data
      .map(
        (item) => `
      <tr>
        <td>${item.designation}</td>
        <td>${formatNumber(item.montant_net)} FCFA</td>
        <td>${formatNumber(item.benefice)} FCFA</td>
        <td>${item.customer?.name ?? '—'}</td>
        <td>${formatDateWithTime(item.date_creation)}</td>
        <td>${statusLabel(item.statut_commande)}</td>
      </tr>`
      )
      .join('');
    printWindow.document.write(`
      <html><head><title>Ventes</title>
      <style>table{width:100%;border-collapse:collapse}th,td{border:1px solid #ccc;padding:8px}</style>
      </head><body><h1>Gestion des ventes</h1><table>
      <thead><tr><th>Référence</th><th>TTC</th><th>Bénéfice</th><th>Client</th><th>Date</th><th>Statut</th></tr></thead>
      <tbody>${tableRows}</tbody></table></body></html>`);
    printWindow.document.close();
    printWindow.print();
  };

  const handleAddDepense = async (e) => {
    e.preventDefault();
    if (!libelle || !montant) {
      Swal.fire({ icon: 'error', title: 'Erreur', text: 'Veuillez remplir tous les champs.' });
      return;
    }
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${APP_API_URL}/commandes/${selectedOrder.id}/add-depense`,
        { libelle, montant },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.status === 200) {
        Swal.fire({ icon: 'success', title: 'Dépense ajoutée', timer: 1500, showConfirmButton: false });
        setLibelle('');
        setMontant('');
        fetchData();
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: error.response?.data?.message || "Erreur lors de l'ajout de la dépense.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const openDepenseModal = (order) => {
    setSelectedOrder(order);
    setShowModal(true);
  };

  const closeDepenseModal = () => {
    setShowModal(false);
    setSelectedOrder(null);
    setLibelle('');
    setMontant('');
  };

  return (
    <ComptableLayout loading={isLoading}>
      <div className="duo-hero duo-hero--compact mb-3">
        <p className="duo-hero-sub mb-1">Comptabilité</p>
        <h1 className="duo-hero-title">Gestion des ventes</h1>
        <p className="duo-hero-sub mt-2 mb-0">
          Ventes approuvées ou livrées et payées — filtres par période comme le reste de l&apos;application.
        </p>
      </div>

      <div className="duo-kpi-row mb-3">
        <div className="duo-kpi-chip">
          <p className="duo-kpi-chip__label">Total TTC (filtre)</p>
          <p className="duo-kpi-chip__value">{formatNumber(metaExtra?.extra?.total_montant_net ?? 0)} FCFA</p>
        </div>
        <div className="duo-kpi-chip">
          <p className="duo-kpi-chip__label">Bénéfice (filtre)</p>
          <p className="duo-kpi-chip__value">{formatNumber(metaExtra?.extra?.total_benefice ?? 0)} FCFA</p>
        </div>
        <div className="duo-kpi-chip">
          <p className="duo-kpi-chip__label">Ventes (filtre)</p>
          <p className="duo-kpi-chip__value">{metaExtra?.extra?.total_commandes ?? 0}</p>
        </div>
      </div>

      <div className="mb-3">
        <DuoPeriodPicker
          options={periodOptionsWithAll}
          selectedOption={selectedPeriod}
          onSelect={handlePeriodSelect}
        />
        {filters.period && (
          <p className="small text-muted mt-2 mb-0">
            Période :{' '}
            <strong>
              {periodOptionsWithAll.find((p) => p.value === filters.period)?.label ?? filters.period}
            </strong>
          </p>
        )}
      </div>

      <section className="duo-panel">
        <div className="duo-toolbar">
          <span className="fw-bold" style={{ color: '#3c3c3c', fontSize: '0.9rem' }}>
            Liste des ventes
          </span>
          <div className="duo-toolbar__actions">
            <button type="button" title="PDF" onClick={exportToPDF} className="duo-btn duo-btn--ghost duo-btn--icon">
              <i className="bi bi-filetype-pdf" />
            </button>
            <button type="button" title="Excel" onClick={exportToExcel} className="duo-btn duo-btn--ghost duo-btn--icon">
              <i className="bi bi-filetype-xls" />
            </button>
            <button type="button" title="Imprimer" onClick={handlePrint} className="duo-btn duo-btn--ghost duo-btn--icon">
              <i className="bi bi-printer" />
            </button>
            <button type="button" title="Rafraîchir" onClick={fetchData} className="duo-btn duo-btn--ghost duo-btn--icon">
              <i className="bi bi-arrow-clockwise" />
            </button>
            <button
              type="button"
              title="Filtres"
              onClick={() => setFiltersOpen(!filtersOpen)}
              className={`duo-btn duo-btn--ghost duo-btn--icon duo-btn--filter${filtersOpen ? ' is-open' : ''}`}
            >
              <i className={`bi ${filtersOpen ? 'bi-x-lg' : 'bi-funnel-fill'}`} />
            </button>
          </div>
        </div>

        {filtersOpen && (
          <div className="duo-filters">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                setCurrentPage(1);
                fetchData();
              }}
            >
              <div className="duo-filters__grid">
                <input
                  type="search"
                  className="duo-input"
                  placeholder="Rechercher référence, client..."
                  value={filters.search}
                  onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
                />
                <input
                  type="date"
                  className="duo-input"
                  value={filters.dateFrom}
                  disabled={!!filters.period}
                  onChange={(e) => {
                    setSelectedPeriod(PERIOD_ALL);
                    setFilters((prev) => ({ ...prev, dateFrom: e.target.value, period: '' }));
                  }}
                />
                <input
                  type="date"
                  className="duo-input"
                  value={filters.dateTo}
                  disabled={!!filters.period}
                  onChange={(e) => {
                    setSelectedPeriod(PERIOD_ALL);
                    setFilters((prev) => ({ ...prev, dateTo: e.target.value, period: '' }));
                  }}
                />
                <select
                  className="duo-select"
                  value={filters.statut}
                  onChange={(e) => setFilters((prev) => ({ ...prev, statut: e.target.value }))}
                >
                  <option value="">Tous les statuts</option>
                  <option value="approuvé">Approuvé</option>
                  <option value="livré">Livré</option>
                </select>
              </div>
              <div className="d-flex justify-content-end gap-2 flex-wrap">
                <button type="button" className="duo-btn duo-btn--ghost" onClick={handleResetFilters}>
                  Réinitialiser
                </button>
                <button type="submit" className="duo-btn duo-btn--primary">
                  <i className="bi bi-search" /> Rechercher
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="duo-table-wrap">
          <table className="duo-table">
            <thead>
              <tr>
                <th onClick={() => requestSort('designation')}>
                  Référence <SortIcon active={sortConfig.key === 'designation'} direction={sortConfig.direction} />
                </th>
                <th onClick={() => requestSort('montant_net')}>
                  Montant TTC <SortIcon active={sortConfig.key === 'montant_net'} direction={sortConfig.direction} />
                </th>
                <th onClick={() => requestSort('benefice')}>
                  Bénéfice <SortIcon active={sortConfig.key === 'benefice'} direction={sortConfig.direction} />
                </th>
                <th onClick={() => requestSort('user_name')}>
                  Client <SortIcon active={sortConfig.key === 'user_name'} direction={sortConfig.direction} />
                </th>
                <th onClick={() => requestSort('created_at')}>
                  Date <SortIcon active={sortConfig.key === 'created_at'} direction={sortConfig.direction} />
                </th>
                <th onClick={() => requestSort('statut_commande')}>
                  Statut <SortIcon active={sortConfig.key === 'statut_commande'} direction={sortConfig.direction} />
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.length > 0 ? (
                data.map((item) => (
                  <tr key={item.id}>
                    <td className="fw-semibold">{item.designation}</td>
                    <td className="text-nowrap">{formatNumber(item.montant_net)} FCFA</td>
                    <td className="text-nowrap">{formatNumber(item.benefice)} FCFA</td>
                    <td>{item.customer?.name ?? '—'}</td>
                    <td className="text-nowrap">{formatDateWithTime(item.date_creation)}</td>
                    <td>
                      <span className={statusBadgeClass(item.statut_commande)}>
                        {statusLabel(item.statut_commande)}
                      </span>
                    </td>
                    <td>
                      <div className="duo-table__actions">
                        <Link
                          to={`/commande/${item.designation}`}
                          title="Voir facture"
                          className="duo-btn duo-btn--ghost duo-btn--sm"
                        >
                          <i className="bi bi-pencil-square" />
                        </Link>
                        {!isRole13 && (
                          <button
                            type="button"
                            onClick={() => openDepenseModal(item)}
                            title="Dépenses"
                            className="duo-btn duo-btn--blue duo-btn--sm"
                          >
                            <i className="bi bi-currency-exchange" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="duo-empty-row">
                    Aucune vente pour cette période.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {(responseMeta?.total ?? 0) > 0 && (
          <div className="duo-footer-bar">
            <span>
              {responseMeta.from} – {responseMeta.to} sur {responseMeta.total} vente
              {responseMeta.total > 1 ? 's' : ''}
            </span>
            <ul className="duo-pagination">
              <li>
                <button
                  type="button"
                  className="duo-btn duo-btn--ghost"
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange('prev')}
                >
                  Précédent
                </button>
              </li>
              <li>
                <span className="duo-pagination__info px-2">
                  Page {currentPage} / {lastPage}
                </span>
              </li>
              <li>
                <button
                  type="button"
                  className="duo-btn duo-btn--primary"
                  disabled={currentPage >= lastPage}
                  onClick={() => handlePageChange('next')}
                >
                  Suivant
                </button>
              </li>
            </ul>
          </div>
        )}
      </section>

      {showModal && selectedOrder && (
        <DuoModal
          size="lg"
          title={`Dépenses — ${selectedOrder.designation}`}
          onClose={closeDepenseModal}
          footer={
            <button type="button" className="duo-btn duo-btn--ghost" onClick={closeDepenseModal}>
              Fermer
            </button>
          }
        >
          {selectedOrder.depenses?.length > 0 ? (
            <ul className="list-unstyled mb-4 p-0">
              {selectedOrder.depenses.map((depense, index) => (
                <li key={index} className="duo-balance-box mb-2" style={{ listStyle: 'none' }}>
                  <strong>{depense.libelle}</strong>
                  <br />
                  Montant : {formatNumber(depense.montant_depense)} FCFA
                  <br />
                  Date : {formatDateWithTime(depense.date)}
                </li>
              ))}
            </ul>
          ) : (
            <p className="duo-empty-row" style={{ padding: '1rem' }}>
              Aucune dépense enregistrée.
            </p>
          )}
          <h3 className="duo-section-title">Ajouter une dépense</h3>
          <form onSubmit={handleAddDepense}>
            <div className="duo-form-group">
              <label className="duo-form-label" htmlFor="libelle">
                Libellé
              </label>
              <input
                id="libelle"
                type="text"
                className="duo-input"
                value={libelle}
                onChange={(e) => setLibelle(e.target.value)}
                required
              />
            </div>
            <div className="duo-form-group">
              <label className="duo-form-label" htmlFor="montant">
                Montant
              </label>
              <input
                id="montant"
                type="number"
                className="duo-input"
                value={montant}
                onChange={(e) => setMontant(e.target.value)}
                min="0"
                required
              />
            </div>
            <button type="submit" className="duo-btn duo-btn--primary w-100" disabled={isLoading}>
              Ajouter dépense
            </button>
          </form>
        </DuoModal>
      )}
    </ComptableLayout>
  );
}
